import {
    ChangeDetectionStrategy,
    DestroyRef,
    type DestroyableInjector,
    Component,
    Injector,
    ElementRef,
    inject,
    signal,
    computed,
    viewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type { DialogResult } from '../dialog';
import { DialogContent } from '../dialog';
import { FilePreview } from './file-preview';
import type {
    FilePreviewDialogData,
    FilePreviewPanelContent,
    FilePreviewPanelSide,
} from './models';

@Component({
    selector: 'qpx-file-preview-dialog',
    imports: [DialogContent, FilePreview],
    templateUrl: './file-preview-dialog.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'flex h-full min-h-0 flex-col overflow-hidden',
    },
})
export class FilePreviewDialog {
    //#region Dependency injection
    private readonly injector = inject(Injector);
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly dialogRef =
        inject<MatDialogRef<FilePreviewDialog, DialogResult>>(MatDialogRef);
    protected readonly data = inject<FilePreviewDialogData>(MAT_DIALOG_DATA);
    private readonly preview = viewChild(FilePreview);
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region State
    private panelInjector: DestroyableInjector | null = null;
    protected readonly panelContent = signal<FilePreviewPanelContent>(
        this.resolvePanelContent(this.data)
    );
    protected readonly source = signal<FilePreviewDialogData>({
        previewUrl: this.data.previewUrl,
        refreshPreviewUrl: this.data.refreshPreviewUrl,
        fileName: this.data.fileName,
        initialPage: this.data.initialPage,
        actions: this.data.actions,
        searchTerm: this.data.searchTerm,
    });
    protected readonly actions = computed(() => this.source().actions ?? []);
    //#endregion

    constructor() {
        this.destroyRef.onDestroy(() => this.panelInjector?.destroy());
    }

    public openPanel(id: string, side: FilePreviewPanelSide): void {
        this.preview()?.openPanel(id, side);
    }

    public showSource(source: FilePreviewDialogData): void {
        if (
            'panels' in source ||
            'infoPanel' in source ||
            'panelContentFactory' in source
        ) {
            this.panelContent.set(this.resolvePanelContent(source));
        }
        this.source.set({
            ...source,
            actions: source.actions ?? this.source().actions,
        });
        if (!this.data.ariaLabel) {
            this.elementRef.nativeElement
                .closest('[role="dialog"]')
                ?.setAttribute('aria-label', `Preview ${source.fileName}`);
        }
    }

    private resolvePanelContent(
        data: FilePreviewDialogData
    ): FilePreviewPanelContent {
        const previous = this.panelInjector;
        const next = data.panelContentFactory
            ? Injector.create({ providers: [], parent: this.injector })
            : null;
        try {
            const content = data.panelContentFactory?.(next ?? this.injector);
            this.panelInjector = next;
            previous?.destroy();
            return {
                panels: content?.panels ?? data.panels ?? [],
                infoPanel: content?.infoPanel ?? data.infoPanel,
            };
        } catch (cause: unknown) {
            next?.destroy();
            throw cause;
        }
    }

    //#region Template methods
    protected close(): void {
        this.dialogRef.close();
    }
    //#endregion
}
